import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getAPI, postAPI } from '../services/api.service';
import { useNavigate } from 'react-router-dom';

type UseAcceptInviteOpts = {
    userId: string;
    groupId: string;
    // optional success callback
    onSuccess?: (inviteData?: any) => void;
};

export function useAcceptInvite({ userId, groupId, onSuccess }: UseAcceptInviteOpts) {
    const [groupTitle, setGroupTitle] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [fetchingGroup, setFetchingGroup] = useState<boolean>(false);
    const [code, setCode] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const navigate = useNavigate();

    // basic validation for 6-digit numeric code
    const validateCode = useCallback((c: string) => {
        const v = String(c).trim();
        if (!v) return 'Enter the 6-digit code';
        if (!/^\d{6}$/.test(v)) return 'Code must be 6 digits';
        return null;
    }, []);

    // fetch group metadata (title) to display on screen
    useEffect(() => {
        let mounted = true;
        if (!groupId) {
            setGroupTitle('');
            return;
        }
        (async () => {
            try {
                setFetchingGroup(true);
                // try getting group by id - adjust endpoint if your API differs
                const res = await getAPI<{ data: any, message: string }>(`groups/${encodeURIComponent(groupId)}`, { method: 'GET' });
                if (res?.status !== 200) {
                    // fallback: try a metadata endpoint or leave the id shown
                    setGroupTitle('');
                    setFetchingGroup(false);
                    return;
                }
                const g = res?.data ?? null;
                const title = (g && (g.data?.title));
                if (mounted) setGroupTitle(String(title || ''));
            } catch (err) {
                // ignore silently; leave title blank
            } finally {
                if (mounted) setFetchingGroup(false);
            }
        })();
        return () => { mounted = false; };
    }, [groupId]);

    const submit = useCallback(async () => {
        setError(null);
        setSuccess(null);

        const v = validateCode(code);
        if (v) {
            setError(v);
            return false;
        }
        if (!userId || !groupId) {
            setError('Missing userId or groupId; ensure you opened the correct invite link.');
            return false;
        }

        setLoading(true);
        try {
            const payload = { userId: String(userId), groupId: String(groupId), code: String(code).trim() };
            const res = await postAPI<{ data: any, message: string }>('groups/accept-invite', payload);

            if (res.status != 200) {
                const msg = res?.error|| "Unable to verify code.";
                toast.error(String(msg));
                setLoading(false);
                return false;
            }

            // success
            const msg = res?.mesage || 'Invite accepted';
            setSuccess(msg);
            toast.success(msg);
            setLoading(false);
            if (onSuccess) onSuccess(res?.data);

            // check whether the participantActive is true
            if(res?.data?.data?.invite?.participantActive){

                if(localStorage?.getItem('user')){
                    const user = JSON.parse(localStorage.getItem('user') || '{}') as { id?: string; role?: string; participantActive?: boolean };
                    user.participantActive = true;
                    localStorage.setItem('user', JSON.stringify(user));
                navigate(`${process.env.PUBLIC_URL || ''}/group/participants`);
                }else{
                    navigate(`/login`);
                }
            }else{
                navigate(`/group/set-password`, { state: { userId: String(userId), groupId: String(groupId) } });
            }
            
            return true;
        } catch (err: any) {
            console.error('accept-invite error', err);
            const msg = err?.message || 'Network error';
            setError(msg);
            toast.error(msg);
            setLoading(false);
            return false;
        }
    }, [code, userId, groupId, validateCode, onSuccess]);

    const reset = useCallback(() => {
        setCode('');
        setError(null);
        setSuccess(null);
    }, []);

    return {
        // state
        groupTitle,
        fetchingGroup,
        loading,
        code,
        error,
        success,
        // actions
        setCode,
        submit,
        reset
    };
}
